import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRedo } from '@fortawesome/pro-solid-svg-icons';
import { getFuzzyDate } from 'utils';
import { monetizeWithDecimal } from 'common/utils/strings';
import Skeleton from 'react-loading-skeleton';
import { ModalTrackDialog } from './ModalTrackDialog';
import { Card, CardBalance as ICardBalance } from 'common/api/e-comm/models/Card';
import { APIResponse, APIError } from 'common/api/models';
import { LinkBtn } from 'components/link-btn';
import { ProductType } from 'common/api/e-comm/models/Product';
import { Modal, ModalPrimaryBtn, ModalSecondaryBtn } from 'components/modal/Modal';
import { colors } from 'styles/settings';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { ReactComponent as WarningTriangleFilled } from '../../../media/images/warning-triangle-filled.svg';

export type CardBalanceErrorType = 'IncorrectPin' | 'MissingPin' | 'GenericApiError';

export const SmallWarningTriangle = () => (
    <WarningTriangleFilled width={16} height={16} className="inline-block" />
);

const GenericPinError = ({ refreshBalance }: { refreshBalance: () => void }) => {
    return (
        <div className="text-error text-sm">
            <SmallWarningTriangle /> Error updating card balance, please try again.{' '}
            <LinkBtn onClick={refreshBalance}>Retry</LinkBtn>
        </div>
    );
};

const MissingPinError = ({ onEditCardDetails }: { onEditCardDetails: () => void }) => {
    return (
        <div className="text-error text-sm">
            <SmallWarningTriangle /> This card's PIN is missing. To view your current balance or add
            money to this card, <LinkBtn onClick={onEditCardDetails}>Edit Card Details.</LinkBtn>
        </div>
    );
};

const IncorrectPinError = ({ onEditCardDetails }: { onEditCardDetails: () => void }) => {
    return (
        <div className="text-error text-sm">
            <SmallWarningTriangle /> This card's PIN is incorrect. To view your current balance or
            add money to this card,{' '}
            <LinkBtn onClick={onEditCardDetails}>Edit Card Details.</LinkBtn>
        </div>
    );
};

export const CardBalance = ({
    card,
    loadCard,
    onAddMoneyClick,
    isLoading,
    balance,
    cardBalanceError,
    shouldHideCardBalance,
    onEditCardDetails,
    cardBalanceErrorType,
    setCardBalanceErrorType,
}: {
    card: Card | null;
    loadCard: (_: string) => Promise<APIResponse<Card>>;
    onAddMoneyClick: () => void;
    isLoading: boolean;
    balance?: ICardBalance | null;
    cardBalanceError: APIError<null> | null;
    shouldHideCardBalance: boolean;
    onEditCardDetails: () => void;
    cardBalanceErrorType?: CardBalanceErrorType;
    setCardBalanceErrorType: (value: CardBalanceErrorType | undefined) => void;
}) => {
    const [lastUpdate, setLastUpdate] = useState<Date>();

    useEffect(() => {
        if (!cardBalanceError) {
            setCardBalanceErrorType(undefined);
            return;
        }

        const statusError = cardBalanceError?.response?.status || 0;
        const failedWith403 = statusError === 403;
        const isPinRequiredError = failedWith403 && !card?.pin;
        const isPinIncorrectError = failedWith403 && card?.pin;

        if (isPinRequiredError) {
            setCardBalanceErrorType('MissingPin');
            return;
        }

        if (isPinIncorrectError) {
            setCardBalanceErrorType('IncorrectPin');
            return;
        }

        if (!cardBalanceError.response?.ok) {
            setCardBalanceErrorType('GenericApiError');
            return;
        }
    }, [cardBalanceError, setCardBalanceErrorType, card?.pin]);

    const [showTrackBalanceDialog, setShowTrackBalanceDialog] = useState(false);
    const cardId = card?.id || null;

    const [ticker, setTicker] = useState(0);
    const balanceUpdated = card?.balanceUpdated;

    useEffect(() => {
        if (cardId) {
            setLastUpdate(balanceUpdated ? new Date(balanceUpdated) : undefined);
        }
    }, [cardId, balanceUpdated]);

    const refreshBalance = useCallback(async () => {
        if (!cardId) {
            return;
        }

        const response = await loadCard(cardId);

        if (!response.error && !response.data.settings?.canCheckBalance) {
            setLastUpdate(new Date());
            return;
        }
    }, [cardId, loadCard, setLastUpdate]);

    useEffect(() => {
        if (card && card.settings?.canCheckBalance && balance?.balanceUpdated) {
            setLastUpdate(new Date(balance?.balanceUpdated));
        }
    }, [card, balance, setLastUpdate]);

    // Following the same logic from native/src/features/store/features/wallet/hooks/useCardBalance.ts on line 31...
    // The implemented logic is:
    // If we canCheckBalance then it means that the card balance should be retrieved from the getBalance API call,
    // otherwise it should be the balance retrieved from the card object itself (getWalletCardById API call.)
    const cardBalance = useMemo(() => {
        if (!card) {
            return 0;
        }
        // CASE: canCheckBalance is false
        if (!card.settings?.canCheckBalance) {
            return card.cardBalance || 0;
        }

        // CASE: canCheckBalance is true
        return balance?.cardBalance || 0;
    }, [card, balance]);

    useEffect(() => {
        const t = setInterval(() => {
            setTicker((_) => _ + 1);
        }, 60000);
        return () => clearInterval(t);
    }, [setTicker]);

    const canCheckBalance = useMemo(() => card?.settings?.canCheckBalance, [card]);

    const lastUpdateDateFormatted = useMemo(
        () => {
            if (!lastUpdate) {
                return '';
            }

            return !canCheckBalance
                ? `As of ${lastUpdate.toLocaleDateString()}`
                : `Updated ${getFuzzyDate(lastUpdate) || ''}`;
        },
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
        [lastUpdate, ticker]
    );

    const cardBalanceFormatted = useMemo(
        () => monetizeWithDecimal(cardBalance || 0),
        [cardBalance]
    );

    if (shouldHideCardBalance) {
        return <></>;
    }

    return (
        <div>
            <div className="flex justify-between">
                <div className="flex flex-col">
                    <div className="text-2xl font-bold text-brand">
                        {cardBalanceErrorType ? '$--.--' : cardBalanceFormatted}
                    </div>
                    {renderRefreshButton(
                        Boolean(canCheckBalance),
                        isLoading,
                        lastUpdateDateFormatted,
                        refreshBalance
                    )}
                </div>
                {!isLoading && card?.type !== ProductType.EGiftCard && (
                    <button
                        onClick={onAddMoneyClick}
                        className="text-xs underline text-brand font-semibold sm:hidden self-end cursor-pointer"
                    >
                        Add money
                    </button>
                )}
            </div>
            {cardBalanceErrorType === 'MissingPin' ? (
                <MissingPinError onEditCardDetails={onEditCardDetails} />
            ) : null}
            {cardBalanceErrorType === 'IncorrectPin' ? (
                <IncorrectPinError onEditCardDetails={onEditCardDetails} />
            ) : null}
            {cardBalanceErrorType === 'GenericApiError' ? (
                <GenericPinError refreshBalance={refreshBalance} />
            ) : null}
            {!isLoading && !card?.settings?.canCheckBalance && (
                <div className="mt-2">
                    <LinkBtn className="text-xs" onClick={() => setShowTrackBalanceDialog(true)}>
                        Track Balance
                    </LinkBtn>
                </div>
            )}
            {card && (
                <ModalTrackDialog
                    cardId={card.id}
                    currentBalance={cardBalance}
                    onBalanceUpdated={refreshBalance}
                    isOpen={showTrackBalanceDialog}
                    setIsOpen={setShowTrackBalanceDialog}
                />
            )}
            {/* TBD (Check With Reema): Commenting this error modal message for now because it's the same as FEC-1714. */}
            {/* <ErrorModal
                isOpen={Boolean(hasErrorRefreshingBalance)}
                error={hasErrorRefreshingBalance}
                closeModal={() => setHasErrorRefreshingBalance(null)}
                refreshBalance={refreshBalance}
            /> */}
        </div>
    );
};

const ErrorModal = ({
    isOpen,
    closeModal,
    error,
    refreshBalance,
}: {
    isOpen: boolean;
    closeModal: () => void;
    error: null | APIError<null>;
    refreshBalance: () => void;
}) => {
    // TBD/TODO: check if this still works
    const isPinRequiredError = error?.response?.statusText === 'RequiresPinForBalanceAndReload';

    return (
        <Modal title="" isOpen={isOpen} onClose={closeModal}>
            <div className="mt-4 flex flex-col items-center">
                <FontAwesomeIcon color={colors.error} icon={faExclamationTriangle} size="2x" />
                <div className="mt-6 text-center w-auto sm:w-9/12 ">
                    <p className="text-xl sm:text-2xl font-semibold mb-4">
                        {!isPinRequiredError ? 'Could Not Update Balance' : 'PIN Error'}
                    </p>
                    <p className="text-sm text-grey-2 mt-4">
                        {!isPinRequiredError
                            ? 'We’re having trouble updating your balance.'
                            : "This card's PIN is missing or incorrect. To view your current balance or add money to this card, Edit Card Details."}
                    </p>
                    {!isPinRequiredError ? (
                        <p className="text-sm text-grey-2">Please try again.</p>
                    ) : null}
                </div>
                {!isPinRequiredError ? (
                    <div className="mt-9">
                        <ModalPrimaryBtn style={{ minWidth: '60px' }} onClick={refreshBalance}>
                            Retry
                        </ModalPrimaryBtn>
                    </div>
                ) : (
                    <ModalPrimaryBtn
                        className="mt-9 mb-5"
                        style={{ minWidth: '60px' }}
                        onClick={closeModal}
                    >
                        Close
                    </ModalPrimaryBtn>
                )}
            </div>
        </Modal>
    );
};

const renderRefreshButton = (
    canCheckBalance: boolean,
    isLoading: boolean,
    lastUpdateFormatted: string | undefined,
    refreshBalance: () => void
) => (
    <div className="flex">
        {!isLoading && (
            <div className="text-xs text-grey-1 mt-auto">
                {canCheckBalance && (
                    <button onClick={refreshBalance}>
                        <FontAwesomeIcon icon={faRedo} size="sm" className="text-brand mx-1" />
                    </button>
                )}{' '}
                {lastUpdateFormatted}
            </div>
        )}
        <div className="mt-auto">{isLoading && <Skeleton width={100} />}</div>
    </div>
);
